import * as React from 'react';
import { useCallback, useMemo, forwardRef } from 'react';

/** Material UI Packages. */
import { Box, Chip, Switch, Tooltip } from '@mui/material';
import { GridActionsCellItem } from '@mui/x-data-grid-premium';

/** Material UI Icons. */
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CloseIcon from '@mui/icons-material/Close';

/** Redux Packages. */
import { useDispatch, useSelector } from 'react-redux';

/** Redux Reducers. */
import { updateStatusMessage, deleteStatusMessage } from 'shared/store/slices/statusMessageSlice';
import { setSnackbar } from 'shared/store/slices/snackBarSlice';

/** Internal Components. */
import useUserRole from 'shared/hooks/useUserRole';
import { CustomSkeleton2 } from 'shared/ui/Skeleton';
import CustomSnackbar from 'shared/ui/CustomSnackbar';
import ThemeButton from 'shared/ui/Button';
import { MainDatagrid } from 'shared/ui/Datagrid';
import { updateSocketData, deleteSocketData } from 'shared/lib/fetchSocket';
import EditStatusMessagePanel from './EditStatusMessagePanel';
import { deleteBlockReason, isReadOnlyStatusMessage, shipmentTypeCodes } from './statusMessageRules';

/** Blank template opened in the side panel when adding a new status message. */
const EMPTY_STATUS_MESSAGE = {
    content: '',
    content_display: '',
    associate: '',
    active: true,
    warning: false,
    has_todo_rules: false,
    in_use: false,
    is_default: false,
    protected: false,
    types: [],
};

/** IL_Status_Associate field-type key → input field type label shown in the Data Entry grid. */
export const ASSOCIATE_FIELD_TYPE_LABELS = {
    associate_date: 'Date',
    associate_datetime: 'Datetime',
    associate_textfield: 'Text',
    associate_location: 'Location',
    associate_datetime_textfield: 'Datetime + Text',
    associate_datetime_location: 'Datetime + Location',
    associate_date_textfield: 'Date + Text',
    associate_date_location: 'Date + Location',
    associate_textfield_datetime: 'Text + Datetime',
    associate_textfield_date: 'Text + Date',
    associate_location_datetime: 'Location + Datetime',
    associate_location_date: 'Location + Date',
};

/** Forwards Data Grid action props (incl. touchRippleRef) to GridActionsCellItem; no DOM wrapper. */
/**
 * The shipment types a message applies to, one chip each. Reads as a set of tags at a glance,
 * which is what the operator is scanning this column for — a comma-run of six-character codes
 * has to be read word by word.
 *
 * Purely presentational: the cell's sort, filter and export value stays the comma string from
 * valueGetter, so the column still behaves like text everywhere the grid uses the raw value.
 */
const ShipmentTypeChips = ({ codes }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', height: '100%' }}>
        {codes.map((code) => (
            <Chip
                key={code}
                label={code}
                size="small"
                variant="outlined"
                sx={{
                    height: 20,
                    fontSize: 11,
                    fontWeight: 600,
                    color: '#00647e',
                    borderColor: '#00647e',
                    backgroundColor: '#ffffff',
                    '& .MuiChip-label': { px: '6px' },
                }}
            />
        ))}
    </Box>
);

const StatusMessageAction = forwardRef(function StatusMessageAction(
    { title, disabled = false, onClick, ...other },
    ref,
) {
    return (
        <Tooltip title={title}>
            <GridActionsCellItem
                ref={ref}
                {...other}
                aria-disabled={disabled || undefined}
                onClick={disabled ? undefined : onClick}
                sx={disabled ? { opacity: 0.38, cursor: 'default' } : undefined}
            />
        </Tooltip>
    );
});

export default function StatusMessage({ loading, tabValue }) {
    const dispatch = useDispatch();
    const statusMessages = useSelector((state) => state.statusMessages);
    const userRole = useUserRole();

    const [editOpen, setEditOpen] = React.useState(false);
    const [selectedRow, setSelectedRow] = React.useState(null);

    const handleNewClick = useCallback(() => {
        setSelectedRow(EMPTY_STATUS_MESSAGE);
        setEditOpen(true);
    }, []);

    const handleToggle = useCallback(
        (row, field) => {
            const updated = { id: row.id, [field]: !row[field] };
            updateSocketData(dispatch, tabValue, 'row', 'il_status_message', '', updated, (receivedData) => {
                dispatch(updateStatusMessage(receivedData.data));
            });
        },
        [dispatch, tabValue],
    );

    const handleEditClick = useCallback(
        (id) => {
            const row = statusMessages.find((message) => message.id === id);
            setSelectedRow(row);
            setEditOpen(true);
        },
        [statusMessages],
    );

    const handleEditDialogClose = useCallback(() => {
        setEditOpen(false);
        setSelectedRow(null);
    }, []);

    const handleDeleteClick = useCallback(
        (id) => {
            const row = statusMessages.find((message) => message.id === id);
            const blocked = deleteBlockReason(row, userRole);
            if (blocked) {
                dispatch(setSnackbar({ open: true, message: blocked, severity: 'warning' }));
                return;
            }
            if (!window.confirm('Are you sure you want to delete this status message?')) return;
            deleteSocketData(
                dispatch,
                tabValue,
                'row',
                'il_status_message',
                'deleteStatusMessage',
                id,
                (deletedId) => {
                    dispatch(deleteStatusMessage(deletedId ?? id));
                    dispatch(
                        setSnackbar({
                            open: true,
                            message: 'Successfully deleted the status message.',
                            severity: 'success',
                        }),
                    );
                },
            );
        },
        [dispatch, tabValue, statusMessages, userRole],
    );

    const columns = useMemo(
        () => [
            {
                field: 'content',
                headerName: 'Status Message',
                flex: 1,
            },
            {
                field: 'associate',
                headerName: 'Input Field',
                flex: 1,
                valueGetter: (value) => ASSOCIATE_FIELD_TYPE_LABELS[value] ?? value,
            },
            {
                field: 'shipment_types',
                headerName: 'Shipment Type',
                width: 260,
                valueGetter: (value, row) => shipmentTypeCodes(row?.types).join(', '),
                renderCell: (params) => <ShipmentTypeChips codes={shipmentTypeCodes(params.row?.types)} />,
            },
            {
                field: 'active',
                headerName: 'Active',
                width: 100,
                align: 'center',
                headerAlign: 'center',
                renderCell: (params) => (
                    <Switch
                        checked={!!params.value}
                        size="small"
                        onChange={() => handleToggle(params.row, 'active')}
                        sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': { color: '#00647e' },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#00647e' },
                        }}
                    />
                ),
            },
            {
                field: 'warning',
                headerName: 'Warning',
                width: 100,
                align: 'center',
                headerAlign: 'center',
                renderCell: (params) => (
                    <Switch
                        checked={!!params.value}
                        size="small"
                        onChange={() => handleToggle(params.row, 'warning')}
                        sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': { color: '#c62828' },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#c62828' },
                        }}
                    />
                ),
            },
            {
                field: 'is_default',
                headerName: 'Default',
                width: 100,
                align: 'center',
                headerAlign: 'center',

                // Read-only — set by the server on system-provided messages and never editable by users
                // (EditStatusMessagePanel has no field for it, and the server ignores it on create/update).
                renderCell: (params) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        {params.value ? <RadioButtonUncheckedIcon fontSize="small" /> : <CloseIcon fontSize="small" />}
                    </Box>
                ),
            },
            {
                field: 'actions',
                type: 'actions',
                headerName: 'Actions',
                width: 100,
                getActions: ({ id, row }) => [
                    <StatusMessageAction
                        key="edit"
                        title={isReadOnlyStatusMessage(row, userRole) ? 'View details (read-only)' : 'Edit'}
                        icon={<EditIcon />}
                        label="Edit"
                        onClick={() => handleEditClick(id)}
                        color="inherit"
                    />,
                    <StatusMessageAction
                        key="delete"
                        title={deleteBlockReason(row, userRole) ?? 'Delete'}
                        icon={<DeleteIcon />}
                        label="Delete"
                        onClick={() => handleDeleteClick(id)}
                        color="inherit"
                    />,
                ],
            },
        ],
        [handleToggle, handleEditClick, handleDeleteClick, userRole],
    );

    if (loading) return <CustomSkeleton2 />;

    return (
        <>
            <CustomSnackbar />
            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <Box
                    sx={{
                        width: editOpen ? `calc(100% - 400px)` : `calc(100% - 30px)`,
                        flexShrink: 0,
                        ml: '15px',
                        mr: editOpen ? 0 : '15px',
                        transition: 'width 0.3s ease, margin-right 0.3s ease',
                    }}
                >
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                        <ThemeButton text="NEW" icon={<AddIcon />} width="72px" onClick={handleNewClick} />
                    </Box>
                    <MainDatagrid
                        rows={statusMessages}
                        cols={columns}
                        showToolbar="GridToolbar"
                        loading={false}
                        id_status={false}
                    />
                </Box>
                <EditStatusMessagePanel open={editOpen} onClose={handleEditDialogClose} row={selectedRow} />
            </Box>
        </>
    );
}
